import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Knowledge Base - Automation Guide
 */
const AutomationGuidePage = () => {
  const automationTopics = [
    {
      title: "Prior Authorization Automation",
      description: "Streamline the prior authorization process with automated submission and tracking.",
      content: [
        "Configure payer-specific authorization rules",
        "Set up automatic submission triggers",
        "Enable status tracking notifications",
        "Configure appeal workflow automation"
      ]
    },
    {
      title: "Claims Processing Automation",
      description: "Automate claim creation, scrubbing, and submission workflows.",
      content: [
        "Auto-populate claims from encounter data",
        "Configure claim editing rules",
        "Set up batch submission schedules",
        "Enable automatic ERA processing"
      ]
    },
    {
      title: "Denial Management Automation",
      description: "Automatically track, categorize, and initiate appeals for denied claims.",
      content: [
        "Configure denial categorization rules",
        "Set up automatic appeal generation",
        "Enable deadline tracking alerts",
        "Configure escalation workflows"
      ]
    },
    {
      title: "Patient Communication Automation",
      description: "Automate patient outreach for billing and payments.",
      content: [
        "Configure statement generation schedules",
        "Set up payment reminder workflows",
        "Enable payment plan notifications",
        "Configure collection letter sequences"
      ]
    }
  ];

  const bestPractices = [
    {
      title: "Start Small",
      description: "Begin with one automation workflow and expand as you gain confidence."
    },
    {
      title: "Monitor Results",
      description: "Review automation metrics regularly to ensure optimal performance."
    },
    {
      title: "Maintain Exceptions",
      description: "Set up proper exception handling for edge cases that require manual review."
    },
    {
      title: "Keep Rules Updated",
      description: "Regularly review and update automation rules as payer requirements change."
    }
  ];

  return (
    <div className="automation-guide-page">
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#0E3056] to-[#1a4a7a] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/knowledge-base" className="text-blue-300 hover:text-white mb-4 inline-block">
              ← Back to Knowledge Base
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Automation Guide</h1>
            <p className="text-xl text-gray-200">
              Learn how to configure and optimize automated workflows in Videxa.
            </p>
          </div>
        </div>
      </section>

      {/* Automation Topics */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
              Automation Workflows
            </h2>

            <div className="space-y-8">
              {automationTopics.map((topic, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6"
                >
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {topic.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    {topic.description}
                  </p>
                  <div className="bg-white dark:bg-gray-800 rounded p-4">
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                      CONFIGURATION OPTIONS:
                    </h4>
                    <ul className="grid md:grid-cols-2 gap-2">
                      {topic.content.map((item, idx) => (
                        <li key={idx} className="flex items-center text-gray-700 dark:text-gray-300">
                          <span className="text-[#FFC000] mr-2">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
              Best Practices
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {bestPractices.map((practice, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md"
                >
                  <h3 className="text-lg font-semibold text-[#0E3056] dark:text-[#FFC000] mb-2">
                    {practice.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {practice.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-16 bg-[#0E3056] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Help with Automation?</h2>
          <p className="text-gray-200 mb-8 max-w-2xl mx-auto">
            Our team can help you design and implement custom automation workflows.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-[#FFC000] text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
          >
            Contact Support
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AutomationGuidePage;
